import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Globe, Users, MapPin, Radio, Wifi } from 'lucide-react';
import { liveAudienceApi } from '../lib/api';

const API_URL = window.location.origin;

const LiveAudienceTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const globeRef = useRef(null);
  const containerRef = useRef(null);
  const [GlobeComponent, setGlobeComponent] = useState(null);

  // Dynamically import react-globe.gl
  useEffect(() => {
    let mounted = true;
    import('react-globe.gl').then((mod) => {
      if (mounted) setGlobeComponent(() => mod.default);
    }).catch(() => {});
    return () => { mounted = false; };
  }, []);

  // Fetch initial data via HTTP
  const fetchData = useCallback(async () => {
    try {
      const result = await liveAudienceApi.getAudience();
      setData(result);
      setLoading(false);
    } catch (e) {
      setLoading(false);
    }
  }, []);

  // WebSocket connection for real-time updates
  useEffect(() => {
    fetchData();

    // Primary: Poll every 5 seconds for reliable real-time data
    const pollInterval = setInterval(fetchData, 5000);

    // Secondary: Try WebSocket for instant updates
    const wsUrl = API_URL.replace('https://', 'wss://').replace('http://', 'ws://') + '/api/ws/live-audience';
    let ws;
    let reconnectTimer;

    const connect = () => {
      try {
        ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => setConnected(true);
        ws.onmessage = (event) => {
          try {
            const parsed = JSON.parse(event.data);
            setData(parsed);
            setLoading(false);
          } catch (e) {}
        };
        ws.onclose = () => {
          setConnected(false);
          // Don't reconnect aggressively - polling handles it
          reconnectTimer = setTimeout(connect, 15000);
        };
        ws.onerror = () => {
          setConnected(false);
          ws.close();
        };
      } catch (e) {
        // WS not available, polling is primary
      }
    };

    connect();

    return () => {
      clearInterval(pollInterval);
      clearTimeout(reconnectTimer);
      if (ws) ws.close();
    };
  }, [fetchData]);

  const totalViewers = data?.total_viewers || 0;
  const countries = data?.countries || [];
  const cities = data?.cities || [];
  const points = data?.points || [];
  const sessions = data?.sessions || [];

  // Globe point data
  const globePoints = points.map((p, i) => ({
    lat: p.lat,
    lng: p.lng,
    size: 0.4,
    color: '#8B5CF6',
    label: `${p.city}, ${p.country}`,
    film: p.film_title,
  }));

  // Rings for animation effect
  const ringsData = points.map((p) => ({
    lat: p.lat,
    lng: p.lng,
    maxR: 3,
    propagationSpeed: 2,
    repeatPeriod: 1500,
  }));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="live-audience-tab">
      {/* Header Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="admin-card p-5 flex items-center gap-4" data-testid="total-viewers-card">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'rgba(139, 92, 246, 0.15)' }}>
            <Users className="w-6 h-6 text-[#8B5CF6]" />
          </div>
          <div>
            <p className="text-3xl font-bold tracking-tight">{totalViewers}</p>
            <p className="text-xs text-gray-400">Active Users</p>
          </div>
        </div>
        <div className="admin-card p-5 flex items-center gap-4" data-testid="countries-count-card">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'rgba(0, 255, 157, 0.1)' }}>
            <Globe className="w-6 h-6 text-[#00FF9D]" />
          </div>
          <div>
            <p className="text-3xl font-bold tracking-tight">{countries.length}</p>
            <p className="text-xs text-gray-400">Countries</p>
          </div>
        </div>
        <div className="admin-card p-5 flex items-center gap-4" data-testid="cities-count-card">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'rgba(59, 130, 246, 0.1)' }}>
            <MapPin className="w-6 h-6 text-blue-400" />
          </div>
          <div>
            <p className="text-3xl font-bold tracking-tight">{cities.length}</p>
            <p className="text-xs text-gray-400">Cities</p>
          </div>
        </div>
        <div className="admin-card p-5 flex items-center gap-4" data-testid="connection-status-card">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: connected ? 'rgba(0, 255, 157, 0.1)' : 'rgba(239, 68, 68, 0.1)' }}>
            {connected ? <Wifi className="w-6 h-6 text-[#00FF9D]" /> : <Radio className="w-6 h-6 text-red-400" />}
          </div>
          <div>
            <p className="text-sm font-semibold">{connected ? 'Live' : 'Polling'}</p>
            <p className="text-xs text-gray-400">{connected ? 'WebSocket Connected' : 'HTTP Fallback'}</p>
          </div>
        </div>
      </div>

      {/* Globe + Sidebar */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* 3D Globe */}
        <div className="lg:col-span-2 admin-card p-0 overflow-hidden relative" ref={containerRef} style={{ minHeight: '500px' }} data-testid="live-globe">
          {GlobeComponent ? (
            <GlobeComponent
              ref={globeRef}
              globeImageUrl="//unpkg.com/three-globe/example/img/earth-night.jpg"
              bumpImageUrl="//unpkg.com/three-globe/example/img/earth-topology.png"
              backgroundImageUrl=""
              backgroundColor="#0a0520"
              pointsData={globePoints}
              pointAltitude="size"
              pointColor="color"
              pointRadius={0.6}
              pointLabel={(d) => `<div style="background:#1a1040;padding:8px 12px;border-radius:8px;border:1px solid rgba(139,92,246,0.3);font-size:12px;color:#fff">
                <b>${d.label}</b><br/><span style="color:#8B5CF6">${d.film}</span>
              </div>`}
              ringsData={ringsData}
              ringColor={() => '#8B5CF6'}
              ringMaxRadius="maxR"
              ringPropagationSpeed="propagationSpeed"
              ringRepeatPeriod="repeatPeriod"
              atmosphereColor="#8B5CF6"
              atmosphereAltitude={0.15}
              width={containerRef.current?.clientWidth || 700}
              height={500}
              animateIn={true}
            />
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500 text-sm">
              {totalViewers === 0 ? 'No active viewers' : 'Loading 3D Globe...'}
            </div>
          )}
          {totalViewers === 0 && GlobeComponent && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/40 z-10">
              <div className="text-center">
                <Globe className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400 text-sm">No active users right now</p>
                <p className="text-gray-600 text-xs mt-1">Users will appear as dots when they are on the platform</p>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar: Countries & Cities */}
        <div className="space-y-4">
          {/* Countries */}
          <div className="admin-card p-5" data-testid="countries-list">
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <Globe className="w-4 h-4 text-[#8B5CF6]" /> Countries
            </h3>
            {countries.length === 0 ? (
              <p className="text-xs text-gray-500">No viewers yet</p>
            ) : (
              <div className="space-y-2 max-h-[180px] overflow-y-auto pr-1">
                {countries.map((c, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span className="text-gray-300 truncate">{c.country}</span>
                    <span className="text-[#8B5CF6] font-semibold tabular-nums">{c.viewers}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Cities */}
          <div className="admin-card p-5" data-testid="cities-list">
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-blue-400" /> Cities
            </h3>
            {cities.length === 0 ? (
              <p className="text-xs text-gray-500">No viewers yet</p>
            ) : (
              <div className="space-y-2 max-h-[180px] overflow-y-auto pr-1">
                {cities.map((c, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span className="text-gray-300 truncate">{c.city}</span>
                    <span className="text-blue-400 font-semibold tabular-nums">{c.viewers}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Active Sessions */}
          <div className="admin-card p-5" data-testid="active-sessions-list">
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <Radio className="w-4 h-4 text-[#00FF9D]" /> Active Sessions
            </h3>
            {sessions.length === 0 ? (
              <p className="text-xs text-gray-500">No active sessions</p>
            ) : (
              <div className="space-y-2 max-h-[180px] overflow-y-auto pr-1">
                {sessions.map((s, i) => (
                  <div key={i} className="text-xs p-2 rounded-lg bg-white/[0.03]">
                    <div className="flex items-center justify-between">
                      <p className="text-gray-300 font-medium">{s.user_name}</p>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${s.activity === 'watching' ? 'bg-[#8B5CF6]/20 text-[#8B5CF6]' : 'bg-[#00FF9D]/15 text-[#00FF9D]'}`}>
                        {s.activity === 'watching' ? 'Watching' : 'Browsing'}
                      </span>
                    </div>
                    <p className="text-[#8B5CF6] truncate">{s.activity === 'watching' ? s.film_title : (s.page || 'Platform')}</p>
                    <p className="text-gray-500">{s.city}{s.city && s.country ? ', ' : ''}{s.country}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveAudienceTab;
